import { useEffect } from "react";

interface UseIntersectionObserverProps {
  onIntersect: (isIntersecting: boolean) => void;
  options?: IntersectionObserverInit;
  debounceDelay?: number; // Optional debounce delay in ms
}

export const useIntersectionObserver = (
  targetRef: React.RefObject<HTMLElement>,
  { onIntersect, options, debounceDelay = 100 }: UseIntersectionObserverProps
) => {
  useEffect(() => {
    const target = targetRef.current;
    if (!target) return;

    let timeoutId: NodeJS.Timeout | null = null;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          // Debounce the onIntersect call
          if (timeoutId) clearTimeout(timeoutId);

          timeoutId = setTimeout(() => {
            onIntersect(entry.intersectionRatio >= (options?.threshold as number || 0.7)); // Default to 70%
          }, debounceDelay);
        });
      },
      options
    );

    observer.observe(target);

    return () => {
      if (timeoutId) clearTimeout(timeoutId);
      observer.disconnect();
    };
  }, [targetRef, onIntersect, options, debounceDelay]);
};
